import { useEffect, useRef, type ReactNode } from 'react';
import { IconX } from '../icons';
import { useTranslate } from '../i18n/react';
import { IconButton } from './Button';

/**
 * A long form, beside the list rather than on top of it.
 *
 * <p>The design package asks for this over a modal for anything past a couple of fields, and
 * the reason shows on the question library: edit a question in a dialog and the row you were
 * comparing it against disappears behind the overlay.</p>
 *
 * <p>Escape closes it and focus is sent inside on open and back to the page on close, which
 * a plain overlay does not do for free.</p>
 */
export function Drawer({
  open,
  title,
  onClose,
  footer,
  children,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  footer?: ReactNode;
  children: ReactNode;
}) {
  const t = useTranslate();
  const panelRef = useRef<HTMLDivElement>(null);
  const returnTo = useRef<HTMLElement | null>(null);

  /**
   * The latest onClose, without the effect below depending on it.
   *
   * <p>Every caller passes an inline arrow, so onClose is a different function on every
   * render. With it in the dependency list, each keystroke re-ran the effect: the cleanup
   * threw focus back to the button that opened the panel and the effect then moved it to the
   * first field. Typing a second character into any form in a drawer was impossible.</p>
   */
  const latestClose = useRef(onClose);
  useEffect(() => {
    latestClose.current = onClose;
  });

  // Only when the panel opens or closes. Nothing else here should move the focus.
  useEffect(() => {
    if (!open) return;

    returnTo.current = document.activeElement as HTMLElement | null;

    // The first thing to fill in, not the first thing that can be focused. In document
    // order the close button comes first — it sits in the header — so this used to open a
    // form with the X focused, where typing goes nowhere and a space bar closes the panel.
    const body = panelRef.current?.querySelector<HTMLElement>('.nv-drawer__body');
    const firstField = body?.querySelector<HTMLElement>('input, select, textarea');
    (firstField ?? panelRef.current?.querySelector<HTMLElement>('button'))?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') latestClose.current();
    };

    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('keydown', onKey);
      returnTo.current?.focus();
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="nv-drawer-scrim"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="nv-drawer" role="dialog" aria-modal="true" aria-label={title} ref={panelRef}>
        <div className="nv-drawer__head">
          <h2 className="nv-h2">{title}</h2>
          <IconButton label={t('common.close')} icon={IconX} onClick={onClose} />
        </div>

        <div className="nv-drawer__body">{children}</div>

        {footer ? <div className="nv-drawer__foot">{footer}</div> : null}
      </div>
    </div>
  );
}
